import { observer } from 'mobx-react'
import { JSX, useContext, useEffect, useState } from 'react'
import { Header, Icon, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, ONBOARDING_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import {
  ErrorTrans,
  FailureReasonTypes,
  GetFailureDetailstResponse,
} from '@/constants/verifiedTypes'
import { Illustration } from '@/components/Illustration/Illustration'

const FailureIcon = ({
  type,
  step,
  color,
}: {
  type: FailureReasonTypes
  step: string
  color: string
}): JSX.Element | undefined => {
  const component: Record<string, Record<string, JSX.Element>> = {
    'FaceSpoofing.Detected': {
      step1: <Icon type="change" color={color} size="32px" />,
    },
    'FacesMismatch.Detected': {
      step1: <Icon type="document" color={color} size="32px" />,
      step2: <Icon type="id" color={color} size="32px" />,
      step3: <Icon type="change" color={color} size="32px" />,
    },
    'Luminance.TooLow': {
      step1: <Icon type="bulb" color={color} size="32px" />,
      step2: <Icon type="change" color={color} size="32px" />,
    },
    'Liveness.NotDetected': {
      step1: <Icon type="emoji-smile" color={color} size="32px" />,
    },
    'Document.Illegible': {
      step1: <Icon type="camera" color={color} size="32px" />,
      step2: <Icon type="change" color={color} size="32px" />,
    },
    'Document.NotLegible': {
      step1: <Icon type="camera" color={color} size="32px" />,
      step2: <Icon type="change" color={color} size="32px" />,
    },
    'Document.NotSecurityElements': {
      step1: <Icon type="document" color={color} size="32px" />,
      step2: <Icon type="id" color={color} size="32px" />,
    },
    'Document.Expired': {
      step1: <Icon type="calendar" color={color} size="32px" />,
    },
    'Document.Aspect': {
      step1: <Icon type="document" color={color} size="32px" />,
      step2: <Icon type="id" color={color} size="32px" />,
    },
    default: {
      step1: <Icon type="change" color={color} size="32px" />,
      step2: <Icon type="globe" color={color} size="32px" />,
      step3: <Icon type="camera" color={color} size="32px" />,
      step4: <Icon type="monitor" color={color} size="32px" />,
    },
  }

  return component[type]?.[step]
}

interface ErrorPageProps {
  errorReason: FailureReasonTypes
}

export const ErrorSantanderPage = observer(({ errorReason }: ErrorPageProps) => {
  const store = useContext(StoreContext)
  const { verifiedErrorPage: trans } = store.TranslationsState.translations

  const [isLoading, setIsLoading] = useState(true)
  const [awaitingResumption, setAwaitingResumption] = useState<boolean>(false)
  const [errorTrans] = useState<ErrorTrans>(trans[errorReason])

  const getFailureDetails = async () => {
    try {
      const { data } = await axiosInstance.get<GetFailureDetailstResponse>(
        `${API_URL}/${ONBOARDING_SERVICE_PATH}/get-failure-details`,
        {
          withCredentials: true,
        }
      )

      setAwaitingResumption(!!data.awaitingResumption)
    } catch (error) {
      devLog('Error fetching failure details:', error)
    }

    setIsLoading(false)
  }

  useEffect(() => {
    getFailureDetails()
  }, [])

  if (isLoading) {
    return (
      <div className="page-container mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  if (awaitingResumption) {
    return (
      <div className="page-container mobile-m-0">
        <Illustration type="identity-verification-failed" />
        <Header>{errorTrans.title}</Header>

        <div className="enpal__step">
          <div className="enpal__step-icon">
            <Icon type="email" color="var(--icon-main-color, black)" size="32px" />
          </div>
          <div className="enpal__info-text">{errorTrans.description}</div>
        </div>

        {errorTrans.step1 && (
          <>
            <p className="generic__paragraph m-0-0-15">{errorTrans.beforeYouRetry}</p>

            <div className="enpal__step">
              <div className="enpal__step-icon">
                <FailureIcon
                  type={errorReason}
                  step="step1"
                  color="var(--icon-error-color, var(--icon-main-color))"
                />
              </div>
              <div className="enpal__info-text">{errorTrans.step1}</div>
            </div>
          </>
        )}

        {errorTrans.step2 && (
          <div className="enpal__step">
            <div className="enpal__step-icon">
              <FailureIcon
                type={errorReason}
                step="step2"
                color="var(--icon-error-color, var(--icon-main-color))"
              />
            </div>
            <div className="enpal__info-text">{errorTrans.step2}</div>
          </div>
        )}

        {errorTrans.step3 && (
          <div className="enpal__step">
            <div className="enpal__step-icon">
              <FailureIcon
                type={errorReason}
                step="step3"
                color="var(--icon-error-color, var(--icon-main-color))"
              />
            </div>
            <div className="enpal__info-text">{errorTrans.step3}</div>
          </div>
        )}

        {errorTrans.step4 && (
          <div className="enpal__step">
            <div className="enpal__step-icon">
              <FailureIcon
                type={errorReason}
                step="step4"
                color="var(--icon-error-color, var(--icon-main-color))"
              />
            </div>
            <div className="enpal__info-text">{errorTrans.step4}</div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="page-container mobile-m-0">
      <Illustration type="identity-verification-failed" />
      <Header>{trans.notAwaitingResumption.title}</Header>
    </div>
  )
})
